/**
 * Per-tenant branding registry.
 *
 * Single source of truth for proposal theme tokens, logos, icons, and
 * brand copy. Renderers / dev pages call `getTenantBranding(tenantId)`
 * rather than importing tenant modules directly so adding a new tenant
 * is a one-line registry change.
 *
 * Adding a new tenant:
 *   1. Drop assets under `public/branding/{tenantId}/` (logos/, icons/,
 *      fonts/, fonts.css)
 *   2. Create `src/branding/{tenantId}.ts` exporting a `TenantBranding`
 *      bundle — copy `techery-au.ts` as a template
 *   3. Register here in TENANT_BRANDING_REGISTRY
 *
 * Returns `null` for unknown tenants — callers must fall back to the
 * baseline DEFAULT_PROPOSAL_THEME_TOKENS rather than crash.
 */

import { techeryAuBranding, type TenantBranding } from './techery-au';

export type { TenantBranding } from './techery-au';

const TENANT_BRANDING_REGISTRY: Record<string, TenantBranding> = {
  'techery-au': techeryAuBranding,
};

export function getTenantBranding(tenantId: string | undefined | null): TenantBranding | null {
  if (!tenantId) return null;
  return TENANT_BRANDING_REGISTRY[tenantId] ?? null;
}

/**
 * Force-load Poppins (and any future tenant's WOFF2 stack) into the
 * document. Idempotent — multiple calls are no-ops after the first
 * matching <link> is appended.
 *
 * Used by the customer-facing viewer to ensure brand fonts hydrate
 * before the magazine renderer paints. The PDF renderer doesn't go
 * through this — it loads via Google Fonts URL in renderToHtml.ts.
 */
export function ensureTenantFontsLoaded(tenantId: string | undefined | null): void {
  if (typeof document === 'undefined') return; // SSR no-op
  const branding = getTenantBranding(tenantId);
  if (!branding?.fontsCssUrl) return;

  const existing = document.querySelector(
    `link[data-tenant-fonts="${tenantId}"]`
  );
  if (existing) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = branding.fontsCssUrl;
  link.dataset.tenantFonts = tenantId!;
  document.head.appendChild(link);
}
